import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Check, X, RotateCcw } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Generische Status-Änderungs-Buttons für E-Mails, Sipgate-Anrufe und
 * Meiti-Anrufe. Verwendet TanStack-Query Mutation + invalidiert Listen.
 *
 * Gebrauch:
 *   <StatusActions
 *     current={e.status}
 *     options={["new","reviewed","done"]}
 *     labels={{ new: "Neu", reviewed: "Gesichtet", done: "Erledigt" }}
 *     mutate={async (s) => { await fetch(...); }}
 *     invalidateKeys={[["emails"], ["email", id]]}
 *   />
 */
export function StatusActions({
  current,
  options,
  labels,
  mutate,
  invalidateKeys,
  size = "md",
}: {
  current: string
  options: string[]
  labels: Record<string, string>
  mutate: (newStatus: string) => Promise<unknown>
  invalidateKeys: (string | number)[][]
  size?: "sm" | "md"
}) {
  const qc = useQueryClient()
  const [optimistic, setOptimistic] = useState<string | null>(null)
  const m = useMutation({
    mutationFn: (s: string) => mutate(s).then(() => s),
    onMutate: (s) => setOptimistic(s),
    onSuccess: () => {
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }))
    },
    onSettled: () => setOptimistic(null),
  })

  const active = optimistic ?? current
  const textSize = size === "sm" ? "text-[11px]" : "text-[12.5px]"
  const pad = size === "sm" ? "px-2 py-0.5" : "px-2.5 py-1"

  return (
    <div className="inline-flex items-center gap-1 flex-wrap">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          disabled={m.isPending || opt === active}
          onClick={(ev) => {
            ev.stopPropagation()
            ev.preventDefault()
            m.mutate(opt)
          }}
          className={cn(
            "inline-flex items-center gap-1 rounded-md font-medium transition",
            textSize, pad,
            opt === active
              ? "bg-primary text-primary-foreground cursor-default"
              : "bg-secondary text-foreground hover:bg-secondary/70",
            m.isPending && "opacity-60 cursor-wait",
          )}
        >
          {opt === "done" && <Check className="w-3 h-3" />}
          {(opt === "new" || opt === "open") && <RotateCcw className="w-3 h-3" />}
          {opt === "missed" && <X className="w-3 h-3" />}
          {labels[opt] ?? opt}
        </button>
      ))}
      {m.isError && (
        <span className="text-[11px] text-destructive ml-1">
          Fehler: {m.error instanceof Error ? m.error.message : "unbekannt"}
        </span>
      )}
    </div>
  )
}
